import { call, put, takeLatest, select } from 'redux-saga/effects';
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  fetchMeRequest,
  fetchMeSuccess,
  fetchMeFailure,
  // 2FA actions
  twoFactorRequired,
  // Password change actions
  passwordChangeRequired,
  changePasswordExpiredRequest,
  changePasswordExpiredSuccess,
  changePasswordExpiredFailure,
  sendTwoFactorCodeRequest,
  sendTwoFactorCodeSuccess,
  sendTwoFactorCodeFailure,
  verifyTwoFactorCodeRequest,
  verifyTwoFactorCodeFailure,
  resendTwoFactorCodeRequest,
  resendTwoFactorCodeSuccess,
  resendTwoFactorCodeFailure,
  // Selectors
  selectTwoFactorUsername,
  selectTwoFactorPassword,
  selectTwoFactorSelectedMethod,
} from './slice';
import authService from '../../services/authService';
import api from '@utils/api';
import config from '../../config';

const extractErrorMessage = (error) => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.error) return error.error;
  if (error.title && !error.errors) return error.title;
  if (error.errors && typeof error.errors === 'object') {
    const firstKey = Object.keys(error.errors)[0];
    if (firstKey) {
      const value = error.errors[firstKey];
      if (Array.isArray(value) && value.length > 0) {
        return value[0];
      }
      if (typeof value === 'string') {
        return value;
      }
    }
  }
  return null;
};

// Check if error indicates 2FA is required (check requiresTwoFactor flag first, then message)
const is2FARequiredError = (error) => {
  // Check for requiresTwoFactor flag in error response
  if (error && error.requiresTwoFactor === true) {
    return true;
  }

  const errorMessage = extractErrorMessage(error);
  if (!errorMessage) return false;

  const lowerMessage = errorMessage.toLowerCase();
  return (
    // English messages
    lowerMessage.includes('two-factor authentication code is required') ||
    lowerMessage.includes('2fa code required') ||
    lowerMessage.includes('two factor') ||
    lowerMessage.includes('two-factor') ||
    // Vietnamese messages
    lowerMessage.includes('yêu cầu nhập mã xác thực 2 lớp') ||
    lowerMessage.includes('mã xác thực 2 lớp') ||
    lowerMessage.includes('xác thực hai yếu tố')
  );
};

// Extract 2FA methods from error response
const extract2FAMethods = (error) => {
  if (error && Array.isArray(error.methods)) {
    return error.methods;
  }
  return [];
};

/**
 * Worker saga: handles login authentication
 * If 2FA is required, dispatches twoFactorRequired action
 * Otherwise, proceeds with normal login flow
 */
function* handleLogin(action) {
  try {
    const { username, password } = action.payload;
    console.log('[Login Saga] Starting login for:', username);

    // Call actual login API using authService
    const loginResponse = yield call(authService.login, username, password);
    console.log('[Login Saga] Login response:', loginResponse);

    // Check if password change is required
    if (loginResponse && loginResponse.requiresPasswordChange === true) {
      console.log('[Login Saga] Password change required');
      yield put(passwordChangeRequired({
        username,
        message: loginResponse.passwordExpiredMessage || 'Password has expired. Please change your password to continue.',
      }));
      return;
    }

    // authService already stores token and user in cookies
    // Now fetch user data with permissions from /me endpoint
    const meResponse = yield call(authService.fetchMe);
    console.log('[Login Saga] Me response:', meResponse);

    // authService.fetchMe already stores user and permissions in cookies
    // Dispatch success action with user data and permissions
    // Backend may return userInfo, user, or individual fields at root
    const userData = meResponse.userInfo || meResponse.user || meResponse;
    yield put(loginSuccess({
      user: userData,
      permissions: meResponse.permissions || null,
    }));
    console.log('[Login Saga] Login success dispatched');

    // Redirect is handled in the component via useEffect or useNavigate
  } catch (error) {
    console.log('[Login Saga] Login error:', error);
    console.log('[Login Saga] Error message:', extractErrorMessage(error));
    console.log('[Login Saga] Is 2FA required error:', is2FARequiredError(error));

    // Check if the error indicates 2FA is required
    if (is2FARequiredError(error)) {
      const { username, password } = action.payload;
      const methods = extract2FAMethods(error);
      console.log('[Login Saga] 2FA required, methods:', methods);

      // Dispatch 2FA required action with username, password, and methods from BE
      yield put(twoFactorRequired({
        username,
        password,
        methods, // Array from BE: [{ method, key, label, available, maskedContact }]
        maskedContact: null,
      }));
      return;
    }

    const errorMessage = extractErrorMessage(error) || 'Login failed';
    console.log('[Login Saga] Dispatching loginFailure with:', errorMessage);
    yield put(loginFailure(errorMessage));
  }
}

/**
 * Worker saga: handles fetching current user data
 */
function* handleFetchMe(action) {
  try {
    // Call /me API using authService
    const response = yield call(authService.fetchMe);

    // authService already updates cookies with latest permissions
    // Dispatch success action with user data and permissions
    // Backend may return userInfo, user, or individual fields at root
    const userData = response.userInfo || response.user || response;
    yield put(fetchMeSuccess({
      user: userData,
      permissions: response.permissions || null,
    }));
  } catch (error) {
    // Dispatch failure action
    const errorMessage = error.message || error.error;
    yield put(fetchMeFailure(errorMessage));
  }
}

/**
 * Worker saga: handles sending 2FA OTP code
 */
function* handleSendTwoFactorCode(action) {
  try {
    const username = yield select(selectTwoFactorUsername);
    const method = action.payload?.method || (yield select(selectTwoFactorSelectedMethod));

    if (!username) {
      yield put(sendTwoFactorCodeFailure('Session expired. Please login again.'));
      return;
    }

    if (!method) {
      yield put(sendTwoFactorCodeFailure('Please select a verification method.'));
      return;
    }

    // Call send OTP API - method is 1 for SMS, 2 for Email
    const response = yield call(authService.sendOtp, username, method);

    yield put(sendTwoFactorCodeSuccess({
      maskedContact: response.maskedContact || response.maskedPhone || response.maskedEmail,
      resendCountdown: response.resendCountdown || response.cooldown || 0,
      message: response.message,
    }));
  } catch (error) {
    const errorMessage = extractErrorMessage(error) || 'Failed to send verification code';
    yield put(sendTwoFactorCodeFailure(errorMessage));
  }
}

/**
 * Worker saga: handles verifying 2FA code by re-login with OTP
 */
function* handleVerifyTwoFactorCode(action) {
  try {
    const username = yield select(selectTwoFactorUsername);
    const password = yield select(selectTwoFactorPassword);
    const { code, useRecoveryCode = false } = action.payload;

    if (!username || !password) {
      yield put(verifyTwoFactorCodeFailure('Session expired. Please login again.'));
      return;
    }

    if (!code || code.length < 6) {
      yield put(verifyTwoFactorCodeFailure('Please enter a valid 6-digit verification code.'));
      return;
    }

    // Call login with 2FA code
    const loginResponse = yield call(authService.loginWith2FA, username, password, code, useRecoveryCode);

    // If verification successful, fetch user data with permissions
    const meResponse = yield call(authService.fetchMe);

    // Dispatch login success with user data and permissions
    const userData = meResponse.userInfo || meResponse.user || meResponse;
    yield put(loginSuccess({
      user: userData,
      permissions: meResponse.permissions || null,
    }));
  } catch (error) {
    const errorMessage = extractErrorMessage(error) || 'Invalid verification code';
    yield put(verifyTwoFactorCodeFailure(errorMessage));
  }
}

/**
 * Worker saga: handles resending 2FA OTP code
 */
function* handleResendTwoFactorCode(action) {
  try {
    const username = yield select(selectTwoFactorUsername);
    const method = action.payload?.method || (yield select(selectTwoFactorSelectedMethod));

    if (!username) {
      yield put(resendTwoFactorCodeFailure('Session expired. Please login again.'));
      return;
    }

    // Call send OTP API again
    const response = yield call(authService.sendOtp, username, method);

    yield put(resendTwoFactorCodeSuccess({
      resendCountdown: response.resendCountdown || response.cooldown || 0,
      message: response.message,
    }));
  } catch (error) {
    const errorMessage = extractErrorMessage(error) || 'Failed to resend verification code';
    yield put(resendTwoFactorCodeFailure(errorMessage));
  }
}

/**
 * Worker saga: handles changing password when expired
 */
function* handleChangePasswordExpired(action) {
  try {
    const { username, currentPassword, newPassword } = action.payload;

    // Call change password API
    const response = yield call(
      api.post,
      config.endpoints.accountManagement.changePasswordExpired,
      {
        username,
        currentPassword,
        newPassword,
      }
    );

    yield put(changePasswordExpiredSuccess());

    // After successful password change, automatically retry login
    yield put(loginRequest({
      username,
      password: newPassword,
    }));
  } catch (error) {
    const errorMessage = extractErrorMessage(error) || 'Failed to change password';
    yield put(changePasswordExpiredFailure(errorMessage));
  }
}

/**
 * Watcher saga: watches for login and 2FA actions
 */
export function* watchLoginActions() {
  yield takeLatest(loginRequest.type, handleLogin);
  yield takeLatest(fetchMeRequest.type, handleFetchMe);
  // 2FA watchers
  yield takeLatest(sendTwoFactorCodeRequest.type, handleSendTwoFactorCode);
  yield takeLatest(verifyTwoFactorCodeRequest.type, handleVerifyTwoFactorCode);
  yield takeLatest(resendTwoFactorCodeRequest.type, handleResendTwoFactorCode);
  // Password change watcher
  yield takeLatest(changePasswordExpiredRequest.type, handleChangePasswordExpired);
}
